import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { useCanalDuCours } from '../features/cours/canal';
import {
  SurfaceDeTrace,
  type CurseurNomme,
  type ElementDeTableau,
  type ImageDeTableau,
  type PenseBeteDeTableau,
} from '../features/cours/moteur';
import { useObservation } from '../features/cours/observation';
import { useScene } from '../features/cours/relecture';
import { lireLErreur } from '../shared/api/client';
import { Bandeau } from '../shared/ui/Bandeau';
import { EtatErreur } from '../shared/ui/EtatErreur';
import { Squelette } from '../shared/ui/Squelette';
import { Statut } from '../shared/ui/Statut';
import { Titre } from '../shared/ui/Titre';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **L'OBSERVATION D'UN COURS EN DIRECT — `CDC-2` F-20, `CDC-5` F-64.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **C'est la seule surface de `web/` qu'un compte qui n'est pas dans le cours atteint**,
 * et la seule que `admin/` ouvre par un renvoi d'URL plutôt qu'en la portant elle-même.
 * Le motif de ce partage vit dans `features/cours/observation.ts` — il tient au port
 * étroit du moteur (D-012), et il est journalisé.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QU'ELLE MONTRE, ET CE QU'ELLE NE MONTRERA JAMAIS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * « Ce qu'un observateur reçoit : **le tableau qui se remplit**, **les curseurs nommés**,
 * les écrans partagés. **Jamais l'image ni le son** » (Q424, Q425, Q427). Le refus de
 * l'image et du son est **explicite aux deux rôles**, et c'est « le refus le plus lourd du
 * domaine » : il vaut aussi pour le compte qui voit tout, dont « l'exception porte sur les
 * cours auxquels il accède et sur son invisibilité, **jamais sur ce qu'il reçoit** ».
 *
 * **AUCUN PANNEAU DE VISIO N'EST MONTÉ ICI, ET C'EST LA FORME QUI TIENT LA RÈGLE.** Ce
 * n'est pas un `if` qu'on pourrait retirer par distraction : le composant n'est pas
 * importé, et **aucune référence de canal de visio ne sort de la réponse d'observation**.
 * Ce qui n'entre pas dans cet écran ne peut pas s'y afficher.
 *
 * **LES ÉCRANS PARTAGÉS NE SONT PAS SERVIS, ET C'EST UN MANQUE NOMMÉ.** Q425 les accorde ;
 * les recevoir demanderait un droit d'entrée au canal de visio, que le contrat réserve aux
 * **présents** du cours. Le contrat n'a donc rien à donner à un observateur, et lui en
 * ouvrir un poserait la question de ce qu'il en reçoit — c'est-à-dire exactement le refus
 * de l'image et du son, à tenir dans un second endroit. **Remonté, pas contourné.**
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **EN LECTURE STRICTE, ET LA SURFACE NE PEUT RIEN ÉCRIRE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * « Il n'écrit pas, ne parle pas, n'allume aucune caméra, ne partage aucun écran, **ne
 * produit aucun curseur**, **n'est jamais compté dans le nombre de flux d'image**, et **ne
 * coupe le flux de personne** » (Q216, Q401, DD-08).
 *
 * **Trois choses le tiennent, et deux sont hors de cet écran.** Le port passe en lecture
 * stricte, donc rien ne se trace et **aucun pointeur ne part** — « sinon l'invisibilité de
 * l'observation du compte qui voit tout tomberait par ce seul chemin » (Q401). Le serveur
 * refuse toute écriture à qui n'est pas présent. Et le registre de présence **ne compte
 * jamais un observateur parmi les présents**, donc il ne paraît ni dans la liste, ni dans
 * l'annonce de présence.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUI RESTE DÛ, ÉCRIT ICI PARCE QUE CET ÉCRAN EST L'ENDROIT OÙ ON LE
 * CHERCHERA.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * - **Les écrans partagés**, ci-dessus.
 * - **Désigner un tracé pour voir son auteur : CE GESTE N'EST PAS ICI, ET C'EST DÉCIDÉ,
 *   PAS OUBLIÉ.** Il est le deuxième de la **liste fermée de quatre de F-47**, qui porte
 *   sur **la relecture d'un cours passé** — et une liste fermée ne s'étend pas à une autre
 *   surface par ressemblance. **F-20 ferme la sienne à ce qu'un observateur REÇOIT** : « le
 *   tableau qui se remplit, les curseurs nommés, les écrans partagés ». L'auteur d'un tracé
 *   déjà posé n'y est pas. **C'est le même refus que la circulation entre les tableaux
 *   ci-dessous, et il vient de la même phrase** (D-127).
 *
 *   **Et ce que ce refus protège se nomme.** « Un observateur n'est jamais un présent » : ce
 *   qu'il reçoit « est du **contenu de travail, jamais une personne** » (Q424). Les curseurs
 *   nommés sont la seule exception, elle est écrite, et le corpus la signale lui-même comme
 *   lourde — « lire qui écrit où, à la seconde, sur de l'écriture manuscrite d'élèves »
 *   (Q401). **Elle porte sur le direct** : un curseur disparaît avec la personne. Attribuer
 *   après coup chaque trait d'une scène est une lecture d'une autre nature, et **aucune
 *   ligne du corpus ne l'accorde à un observateur**. L'accorder ici serait inventer une
 *   règle sur la surface la plus surveillée du produit.
 *
 *   **Ce que ça coûte, écrit plutôt que caché** : la scène lue ici porte `auteurPrenom` —
 *   c'est la même route que la relecture, et elle est **la seule** par laquelle un
 *   observateur apprend quel tableau regarder (Q566, Q567 : « le geste n'a plus qu'une
 *   route »). **La donnée est donc dans la réponse et rien ne l'affiche**, ce qui est
 *   l'état voulu : la restreindre demanderait une seconde route, et une borne qui dépend
 *   du chemin emprunté est « une faille par construction ». **Remonté, pas contourné.**
 * - **La circulation entre les tableaux.** Q430 pose qu'« une structure qui a commencé à
 *   regarder continue jusqu'à la fin du cours » ; rien du corpus ne lui accorde de
 *   circuler, et F-20 ferme sa liste à ce qu'elle **reçoit**. Cet écran suit donc le
 *   tableau vivant, et lui seul.
 * - **La fermeture de la ligne d'observation** est celle de la liaison : elle se ferme
 *   quand cet onglet se ferme, ce qui est le « chemin normal » que
 *   `RepriseDesObservations` annonçait sans qu'il existe.
 */
export function PageObservation() {
  const { coursId } = useParams<{ coursId: string }>();
  const observation = useObservation(coursId ?? '');

  /**
   * **L'observation s'ouvre AVANT le canal, et l'ordre est la règle.**
   *
   * C'est cet appel qui porte la borne de Q567 — « une structure n'observe qu'un cours
   * dont un de ses élèves est présent » —, et c'est la ligne qu'il ouvre que le canal
   * consulte pour admettre. Joindre le salon d'abord ferait un handshake refusé sur une
   * observation parfaitement légitime.
   */
  useEffect(() => {
    if (coursId !== undefined && observation.isIdle) observation.mutate();
  }, [coursId, observation]);

  const canal = useCanalDuCours(
    observation.isSuccess ? (coursId ?? null) : null,
  );

  /**
   * **La scène se lit par le rang que le salon donne**, comme pour un présent : c'est le
   * seul chemin par lequel un observateur apprend quel tableau regarder, `ouvrirUnTableau`
   * en **créant** un et `lireLeTableauCourant` étant réservée aux présents.
   *
   * **Elle laisse sa ligne de trace**, DD-12 : « la lecture d'une scène laisse sa ligne de
   * trace, **quel que soit le lecteur** ». C'est voulu — le passage du compte qui voit
   * tout n'est signalé à personne sur le moment, et **la trace est ce qui le rend
   * constatable après coup** (Q203, Q214).
   */
  const rang = canal.etat.tableauCourantRang;
  const scene = useScene(coursId ?? '', rang ?? 0);

  const elements: readonly ElementDeTableau[] = useMemo(
    () =>
      (scene.data?.traces ?? []).map((t) => t.contenu as ElementDeTableau),
    [scene.data],
  );
  const imagesEnPlace: readonly ImageDeTableau[] = useMemo(
    () =>
      (scene.data?.images ?? []).map((image) => ({
        id: image.id,
        adresse: image.adresse,
        positionX: image.positionX,
        positionY: image.positionY,
        largeur: image.largeur,
        hauteur: image.hauteur,
      })),
    [scene.data],
  );
  const penseBetesEnPlace: readonly PenseBeteDeTableau[] = useMemo(
    () =>
      (scene.data?.penseBetes ?? []).map((note) => ({
        id: note.id,
        texte: note.texte,
        couleur: note.couleur,
        positionX: note.positionX,
        positionY: note.positionY,
      })),
    [scene.data],
  );

  /**
   * **LES CURSEURS NOMMÉS SONT REÇUS, ET C'EST UNE RÈGLE ÉCRITE.**
   *
   * Q401 : « elle voit les curseurs nommés, **comme les présents les voient**. Une
   * personne qui administre une structure, et le compte qui voit tout, lisent donc **qui
   * écrit où, à la seconde**, pendant que le cours a lieu. » Ce que cela ajoute à ce qui
   * était déjà accordé est **l'attribution en direct**, sur de l'écriture manuscrite
   * d'élèves — et c'est signalé au corpus comme tel.
   */
  const curseurs: readonly CurseurNomme[] = useMemo(
    () =>
      [...canal.etat.curseurs.values()].map((c) => ({
        personneId: c.auteurId,
        prenom: c.prenom,
        x: c.x,
        y: c.y,
      })),
    [canal.etat.curseurs],
  );

  /**
   * **« La relecture est refusée sur un tableau sans aucun tracé » (Q321)**, et c'est le
   * cas courant en observation : on arrive souvent sur un tableau que personne n'a encore
   * écrit. Ce n'est donc pas une erreur à montrer comme telle — **une valeur dérivée, pas
   * un état** : la calculer dans un effet la ferait vivre un rendu de retard.
   */
  const rienARelire =
    scene.isError && lireLErreur(scene.error).code === 'TABLEAU_SANS_TRACE';

  if (coursId === undefined) return null;

  return (
    <div className="flex h-dvh flex-col gap-3 bg-tableau-travail p-3">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <span className="flex flex-wrap items-center gap-4">
          <Titre niveau={1} taille="18">
            {observation.data?.titre ?? 'Observation'}
          </Titre>
          <Statut teinte="eteint">Lecture seule</Statut>
          {rang !== null && observation.data !== undefined && (
            <Statut teinte="neutre" pleine={false}>
              {`Tableau ${rang} sur ${observation.data.nombreDeTableaux}`}
            </Statut>
          )}
        </span>
      </header>

      {observation.isPending && <Squelette lignes={3} />}

      {observation.isError && (
        <>
          {lireLErreur(observation.error).code ===
          'AUCUN_ELEVE_DE_CETTE_STRUCTURE' ? (
            <Bandeau ton="echec">
              Aucun élève de votre structure n’est présent sur ce cours : vous
              n’observez que les cours où l’un des vôtres se trouve.
            </Bandeau>
          ) : lireLErreur(observation.error).code === 'COURS_TERMINE' ? (
            <Bandeau ton="attention">
              Ce cours est terminé. L’observation porte sur un cours qui se
              déroule ; un cours terminé se relit par sa scène.
            </Bandeau>
          ) : (
            <EtatErreur
              message={lireLErreur(observation.error).message}
              onReessayer={() => observation.mutate()}
            />
          )}
        </>
      )}

      {observation.data !== undefined && (
        <>
          {/* ═══════════════════════════════════════════════════════════════════
              **CE QUE L'OBSERVATEUR NE RECEVRA JAMAIS, DIT À L'OBSERVATEUR.**
              ═══════════════════════════════════════════════════════════════════

              `CDC-0` §8 : « le produit **explique ses refus, partout** ». Un écran qui
              montrerait un tableau sans un mot laisserait chercher le son et l'image, et
              conclure à une panne là où il y a une règle. */}
          <Bandeau ton="accent">
            Vous voyez le tableau et les curseurs nommés. L’image et le son ne
            sont jamais transmis, à aucun des deux rôles. Votre passage laisse
            une ligne.
          </Bandeau>

          {/* **Savoir qu'on est vu change ce qu'on fait, et rien d'autre ne le dirait.**
              « Le passage d'un `orgadmin` est signalé par un bandeau ; celui du compte
              qui voit tout ne l'est pas » (Q213, Q214). */}
          <Bandeau ton={observation.data.signalee ? 'attention' : 'accent'}>
            {observation.data.signalee
              ? 'Les présents voient un bandeau qui dit qu’une personne de la structure regarde. Il ne vous nomme pas et ne dit pas combien.'
              : 'Votre passage n’est pas signalé aux présents. Il reste constatable après coup par sa trace.'}
          </Bandeau>

          {canal.etat.coupeDepuisSecondes !== null && (
            <Bandeau ton="attention">
              Le direct est interrompu. Ce que vous voyez date de votre dernière
              liaison.
            </Bandeau>
          )}

          {rang === null && !canal.etat.salonRecu && <Squelette lignes={2} />}

          {rienARelire && (
            <Bandeau ton="attention">
              Ce tableau ne porte encore aucun tracé. Ce qui s’écrira à partir de
              maintenant paraît en direct.
            </Bandeau>
          )}

          {rang !== null && (
            <SurfaceDeTrace
              elementsEnPlace={elements}
              elementsRecus={canal.etat.recus}
              imagesEnPlace={imagesEnPlace}
              penseBetesEnPlace={penseBetesEnPlace}
              curseurs={curseurs}
              outil="selection"
              encre="noir"
              epaisseur={3}
              // **Le gel n'a rien à dire ici** : un observateur n'écrit pas parce qu'il
              // observe, jamais parce que le tableau est plein.
              gele={false}
              lectureStricte
              onPose={() => undefined}
              onRetrait={() => undefined}
              onPointeur={() => undefined}
              onPret={() => undefined}
            />
          )}

          {/* **Aucun geste de sortie, et c'est voulu** : « une structure qui a commencé à
              regarder continue jusqu'à la fin du cours » (Q430). Ce qui ferme
              l'observation est la fermeture de l'onglet, qui ferme la liaison, qui ferme
              la ligne. Un bouton « arrêter » ferait croire à un droit de reprendre. */}
          <p className="text-12 text-texte-faible">
            Fermez cet onglet pour arrêter d’observer.
          </p>
        </>
      )}
    </div>
  );
}
